import type { StatusId } from '@jianghu/game-data';
import type { DerivedStatsDTO, DialogOptionDTO, InvItemDTO, QuestDTO, ShopDTO } from '@jianghu/protocol';
import { create } from 'zustand';

export interface HudSelf {
  entityId: number;
  name: string;
  sect: string;
  level: number;
  xp: number;
  xpNext: number;
  coins: number;
  hp: number;
  maxHp: number;
  mp: number;
  maxMp: number;
  stamina: number;
  dead: boolean;
  deadAt: number;
  statuses: [StatusId, number][];
  isAdmin: boolean;
}

export interface HudTarget {
  id: number;
  kind: 'monster' | 'npc' | 'player';
  name: string;
  level?: number;
  hp: number;
  maxHp: number;
  statusBits: number;
  dead: boolean;
}

export interface ChatLine {
  id: number;
  ch: 'zone' | 'system' | 'combat';
  from?: string;
  text: string;
  at: number;
}

export interface Toast {
  id: number;
  kind: 'info' | 'error' | 'loot' | 'quest' | 'level';
  text: string;
  at: number;
  /** Optional identity so a toast can be withdrawn early when it stops being true. */
  key?: string;
}

export interface DialogState {
  npc: number;
  npcId: string;
  name: string;
  title: string;
  lines: string[];
  options: DialogOptionDTO[];
}

export type WindowName = 'inventory' | 'character' | 'quests' | 'map' | 'menu' | 'waypoints' | 'help';

export interface InteractHint {
  key: string;
  label: string;
}

export interface GameUiState {
  connection: 'connecting' | 'online' | 'reconnecting' | 'offline';
  ping: number;
  fps: number;
  zoneName: string;
  self: HudSelf | null;
  stats: DerivedStatsDTO | null;
  target: HudTarget | null;
  inventory: InvItemDTO[];
  quests: QuestDTO[];
  skills: string[];
  /** Cooldown end times in performance.now() milliseconds, with the total duration for the sweep. */
  cooldowns: Record<string, { end: number; total: number }>;
  chat: ChatLine[];
  toasts: Toast[];
  dialog: DialogState | null;
  shop: ShopDTO | null;
  windows: Record<WindowName, boolean>;
  area: { name: string; at: number } | null;
  interact: InteractHint | null;
  kicked: string | null;
  loading: boolean;
  castBar: { name: string; start: number; end: number } | null;
}

let seq = 1;

const initial = (): GameUiState => ({
  connection: 'connecting',
  ping: 0,
  fps: 0,
  zoneName: '',
  self: null,
  stats: null,
  target: null,
  inventory: [],
  quests: [],
  skills: [],
  cooldowns: {},
  chat: [],
  toasts: [],
  dialog: null,
  shop: null,
  windows: {
    inventory: false,
    character: false,
    quests: false,
    map: false,
    menu: false,
    waypoints: false,
    help: false,
  },
  area: null,
  interact: null,
  kicked: null,
  loading: true,
  castBar: null,
});

export const useGame = create<GameUiState>(() => initial());

export const gameUi = {
  reset(): void {
    useGame.setState(initial(), true);
  },
  chat(line: Omit<ChatLine, 'id'>): void {
    useGame.setState((s) => ({ chat: [...s.chat.slice(-79), { ...line, id: seq++ }] }));
  },
  toast(kind: Toast['kind'], text: string, key?: string): void {
    const t: Toast = { id: seq++, kind, text, at: Date.now(), ...(key ? { key } : {}) };
    useGame.setState((s) => ({ toasts: [...s.toasts.slice(-5), t] }));
    window.setTimeout(
      () => useGame.setState((s) => ({ toasts: s.toasts.filter((x) => x.id !== t.id) })),
      kind === 'error' ? 2500 : 4200,
    );
  },
  dismissToast(key: string): void {
    useGame.setState((s) => ({ toasts: s.toasts.filter((x) => x.key !== key) }));
  },
  toggle(w: WindowName, open?: boolean): void {
    useGame.setState((s) => ({ windows: { ...s.windows, [w]: open ?? !s.windows[w] } }));
  },
  closeAll(): boolean {
    const s = useGame.getState();
    const anyOpen = Object.values(s.windows).some(Boolean) || s.dialog !== null || s.shop !== null;
    useGame.setState({
      windows: {
        inventory: false,
        character: false,
        quests: false,
        map: false,
        menu: false,
        waypoints: false,
        help: false,
      },
      dialog: null,
      shop: null,
    });
    return anyOpen;
  },
};
